import { inject, isDevMode } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { JwtService } from '@app/core/auth';
import { map, catchError, of } from 'rxjs';
import { EmbedService } from '../../../services/embed.service';

/**
 * Auth Guard - Validates JWT token before allowing route access
 *
 * SECURITY:
 * - Checks token expiration locally first (faster)
 * - Falls back to API verification if local check fails
 * - Redirects to unauthorized page on any failure
 */

/**
 * Vérifie si le token est présent et non expiré localement
 */
function isTokenValidLocally(jwtService: JwtService, embedService: EmbedService): boolean {
  const token = jwtService.getToken();
  if (!token) {
    return false;
  }

  const decodedToken = jwtService.decodeToken();
  if (!decodedToken) {
    return false;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const isExpired = decodedToken.exp < currentTime;

  if (isExpired) {
    if (isDevMode()) {
      console.log('[AuthGuard] Token expired locally');
    }
    return false;
  }

  checkEnableEmbed(embedService);
  return true;
}

function checkEnableEmbed(embedService: EmbedService): void {
  const embedIsSet = embedService.getEmbedFromUrl();
  if (embedIsSet) {
    embedService.setEmbed(true);
  }
}

export const authGuard: CanActivateFn = (route, state) => {
  const jwtService = inject(JwtService);
  const embedService = inject(EmbedService);

  // Vérifier d'abord si le token est valide localement
  if (!isTokenValidLocally(jwtService, embedService)) {
    // Si le token n'est pas valide localement, faire appel à l'API
    return jwtService.checkTokenAndRedirect().pipe(
      map(isValid => {
        if (!isValid) {
          jwtService.redirectLoginPage();
          return false;
        }

        checkEnableEmbed(embedService);
        return true;
      }),
      catchError(() => {
        jwtService.removeToken();
        jwtService.redirectLoginPage();
        return of(false);
      })
    );
  }

  // Si le token est valide localement, on peut directement retourner true
  return of(true);
};
